import React from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Calendar,
  Clock,
  Sparkles,
  CheckCircle2,
  ExternalLink,
  PhoneCall,
  MessageCircle,
  Award,
  Globe2,
  GraduationCap,
  ArrowRight,
  HelpCircle,
} from "lucide-react";
import SectionTitle from "@/components/SectionTitle";
import { WHATSAPP_NUMBERS } from "@/lib/config";

const GOOGLE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSdYdmOIO36KHz-PA0LCuA2pxHMgEvnBcF1u50ZgTv1ByEGkVw/viewform?usp=sharing&ouid=104753931632940977221";
const PHONE_NUMBER = "+91 94661 45196";
const RAW_PHONE = "919466145196";

const COURSE_LEVELS = [
  {
    id: "level-1",
    tag: "Beginner Level",
    title: "Level 1 — Basic Hangul & Essentials",
    desc: "Master the Korean alphabet (Hangul), basic sentence structures, everyday conversation, and foundational vocabulary tailored for TOPIK I Level 1 proficiency.",
    highlights: [
      "Hangul Reading, Writing & Pronunciation",
      "Essential Daily Greetings & Grammar Patterns",
      "Self-Introductions & Basic Vocabulary (500+ Words)",
      "TOPIK I Level 1 Exam Orientation & Practice",
    ],
    featured: false,
  },
  {
    id: "level-2",
    tag: "Elementary Level",
    title: "Level 2 — Intermediate Grammar & Fluency",
    desc: "Expand your fluency with complex sentence structures, honorifics, daily task conversations, and academic vocabulary for TOPIK I Level 2 certification.",
    highlights: [
      "Advanced Conjugations & Honorific Expressions",
      "Public Speaking, Listening & Comprehension Practice",
      "University Application & Visa Interview Prep",
      "TOPIK I Level 2 Exam Strategy & Mock Tests",
    ],
    featured: false,
  },
  {
    id: "combo-level",
    tag: "Recommended Package",
    badge: "Both Levels Included",
    title: "Level 1 + Level 2 Complete Combo",
    desc: "Our complete end-to-end Korean language training course covering both Level 1 and Level 2 in a unified, structured curriculum for total confidence.",
    highlights: [
      "Complete coverage of Level 1 & Level 2 Curriculum",
      "Comprehensive Study Material & Vocabulary Guides",
      "Dedicated Mentor Support & Personalised Feedback",
      "Priority Guidance for GKS / Korean University Admissions",
    ],
    featured: true,
  },
];

export default function Courses() {
  const waUrl = `https://wa.me/${RAW_PHONE}?text=${encodeURIComponent(
    "Hi, I would like to inquire about the Korean Language Training Program (Level 1 / Level 2 / Combo)."
  )}`;

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-ribbon overflow-hidden" data-testid="courses-hero">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-16 relative">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand/10 text-brand text-[11px] uppercase tracking-[0.2em] font-semibold">
              <Sparkles size={13} /> Language & Academic Programs
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-5 leading-[1.02] text-brand-ink">
              Korean Language <span className="text-brand">Training Program</span>.
            </h1>
            <p className="mt-5 text-brand-muted text-lg leading-relaxed">
              Build language fluency for GKS scholarships, Korean university admissions, and career opportunities. Structured interactive classes designed for beginners and elementary learners.
            </p>

            {/* Timings & Schedule Announcement Pill */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-white border border-brand-line shadow-sm text-sm font-semibold text-brand-ink">
                <Clock size={16} className="text-brand shrink-0" />
                <span>Schedule: <strong>3 days per week (2 hours / session)</strong></span>
              </div>
              <div className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-amber-50 border border-amber-200/80 text-amber-900 text-sm font-medium">
                <Calendar size={16} className="text-amber-600 shrink-0" />
                <span><strong>Notice:</strong> Timings and days will be announced soon.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Levels Offered */}
      <section className="section bg-brand-cream" data-testid="courses-levels">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionTitle
            eyebrow="Curriculum & Levels"
            title="Courses Offered Under Korean Training"
            subtitle="Choose between individual levels or enroll in our complete combo course."
          />

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {COURSE_LEVELS.map((c, i) => (
              <div
                key={c.id}
                className={`relative rounded-3xl border p-8 flex flex-col justify-between transition-all duration-300 ${
                  c.featured
                    ? "bg-brand text-white border-transparent shadow-[0_30px_60px_-20px_rgba(236,92,83,0.55)] hover:-translate-y-1"
                    : "bg-white border-brand-line text-brand-ink hover:-translate-y-1 hover:shadow-[0_28px_50px_-22px_rgba(42,22,20,0.18)] hover:border-brand"
                }`}
                data-testid={`course-card-${c.id}`}
              >
                {c.badge && (
                  <div className="absolute -top-3.5 left-6 bg-brand-ink text-white text-[11px] tracking-[0.18em] uppercase font-semibold px-3.5 py-1 rounded-full shadow-sm">
                    {c.badge}
                  </div>
                )}
                <div>
                  <div
                    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] mb-4 ${
                      c.featured ? "text-white/80" : "text-brand"
                    }`}
                  >
                    <BookOpen size={13} /> {c.tag}
                  </div>
                  <h3 className={`font-display text-2xl font-extrabold leading-snug ${c.featured ? "text-white" : "text-brand-ink"}`}>
                    {c.title}
                  </h3>
                  <p className={`mt-3 text-[14px] leading-relaxed ${c.featured ? "text-white/90" : "text-brand-muted"}`}>
                    {c.desc}
                  </p>

                  <ul className="mt-6 space-y-2.5">
                    {c.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-xs font-medium">
                        <CheckCircle2
                          size={15}
                          className={`mt-0.5 shrink-0 ${c.featured ? "text-white" : "text-brand"}`}
                        />
                        <span className={c.featured ? "text-white/95" : "text-brand-ink/85"}>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 pt-6 border-t border-white/20">
                  <a
                    href={GOOGLE_FORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={`w-full btn-brand text-xs py-3 px-4 inline-flex items-center justify-center gap-2 font-semibold ${
                      c.featured
                        ? "bg-white text-brand hover:bg-brand-cream hover:text-brand-dark"
                        : ""
                    }`}
                    data-testid={`course-register-${c.id}`}
                  >
                    Register for {c.id === "combo-level" ? "Both Levels" : c.tag} <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Details & Schedule Info */}
      <section className="section bg-white" data-testid="courses-details">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <SectionTitle
              eyebrow="Class Structure"
              title="Interactive Learning with Expert Guidance"
              subtitle="Designed to provide maximum practice, interactive speaking exercises, and personal feedback."
            />
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4 p-5 rounded-2xl bg-brand-cream border border-brand-line">
                <div className="w-11 h-11 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0">
                  <Calendar size={20} />
                </div>
                <div>
                  <div className="font-display font-bold text-brand-ink text-base">Weekly Class Days</div>
                  <p className="text-brand-muted text-sm mt-1 leading-relaxed">
                    <strong>3 Days per week</strong> — interactive live sessions with Q&A and practical practice.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 rounded-2xl bg-brand-cream border border-brand-line">
                <div className="w-11 h-11 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0">
                  <Clock size={20} />
                </div>
                <div>
                  <div className="font-display font-bold text-brand-ink text-base">Session Duration</div>
                  <p className="text-brand-muted text-sm mt-1 leading-relaxed">
                    <strong>2 Hours per session</strong> — focused breakdown of vocabulary, grammar rules, and conversational exercises.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-5 rounded-2xl bg-amber-50/80 border border-amber-200/80">
                <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
                  <Sparkles size={20} />
                </div>
                <div>
                  <div className="font-display font-bold text-amber-950 text-base">Batch Timings & Announcement</div>
                  <p className="text-amber-900/90 text-sm mt-1 leading-relaxed">
                    Exact batch timings and weekly class days will be announced soon. Register early via the form below to secure your preferred slot.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form Filling Section */}
          <div className="lg:col-span-6" id="form-section" data-testid="courses-form-section">
            <div className="rounded-3xl bg-brand-ink text-white p-8 sm:p-10 relative overflow-hidden shadow-2xl">
              <div className="absolute inset-0 grain opacity-25" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/20 text-brand text-[11px] uppercase tracking-[0.18em] font-semibold border border-brand/30">
                  <ExternalLink size={12} /> Registration Form
                </div>
                <h3 className="font-display text-3xl font-extrabold mt-4 leading-tight">
                  Form Filling Section
                </h3>
                <p className="mt-3 text-white/80 text-sm leading-relaxed">
                  Fill out our official registration form to submit your enrollment request for Level 1, Level 2, or the Combo course. Our team will contact you directly with batch details.
                </p>

                <div className="mt-8 p-5 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                  <div className="text-xs uppercase tracking-[0.16em] text-white/70 font-semibold mb-2">
                    Official Application Form
                  </div>
                  <div className="text-sm font-mono text-white/90 truncate">
                    https://docs.google.com/forms/...
                  </div>
                </div>

                <div className="mt-8">
                  <a
                    href={GOOGLE_FORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full btn-brand py-4 px-6 text-base font-semibold inline-flex items-center justify-center gap-2 shadow-lg"
                    data-testid="google-form-cta"
                  >
                    Open & Fill Registration Form <ExternalLink size={18} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry Section */}
      <section className="section bg-brand-cream" data-testid="courses-enquiry">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl bg-white border border-brand-line p-8 sm:p-12 text-center shadow-sm relative overflow-hidden">
            <div className="w-16 h-16 rounded-2xl bg-brand/10 text-brand grid place-items-center mx-auto mb-6">
              <HelpCircle size={32} strokeWidth={1.5} />
            </div>

            <SectionTitle
              eyebrow="Have Questions?"
              title="Enquiry Section"
              subtitle="Have doubts about course level selection, batch timings, or GKS preparation? Talk to our course mentors."
              align="center"
              className="mx-auto"
            />

            <div className="mt-8 inline-flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`tel:${PHONE_NUMBER.replace(/\s+/g, "")}`}
                className="btn-brand text-base py-3.5 px-8 inline-flex items-center gap-3"
                data-testid="enquiry-phone-cta"
              >
                <PhoneCall size={20} /> Call Us: {PHONE_NUMBER}
              </a>
              <a
                href={waUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-outline-brand text-base py-3.5 px-8 inline-flex items-center gap-3"
                data-testid="enquiry-whatsapp-cta"
              >
                <MessageCircle size={20} /> WhatsApp Enquiry
              </a>
            </div>

            <div className="mt-6 text-xs text-brand-muted font-medium">
              Available Monday – Saturday · 10:00 AM to 6:00 PM IST
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
